import * as Sentry from '@sentry/react'
import restClient from 'restClient'

export type UtilityTariffByIdState = {
  tariff: Record<string, any> | null
  isLoading: boolean
  error: boolean
}

export const EMPTY_UTILITY_TARIFF_BY_ID_STATE: UtilityTariffByIdState = {
  tariff: null,
  isLoading: false,
  error: false,
}

type Subscriber = (state: UtilityTariffByIdState) => void

type UtilityTariffByIdCacheEntry = {
  state: UtilityTariffByIdState
  subscribers: Set<Subscriber>
  fetchPromise: Promise<void> | null
  hasResolved: boolean
}

export type UtilityTariffFetchClient = ReturnType<typeof restClient>

const utilityTariffByIdCache = new Map<string, UtilityTariffByIdCacheEntry>()

export function utilityTariffByIdCacheKey(orgId: number, utilityTariffId: number): string {
  return `${orgId}:${utilityTariffId}`
}

/** Clears the module cache — for unit tests only. */
export function resetUtilityTariffByIdCache(): void {
  utilityTariffByIdCache.clear()
}

function notifySubscribers(entry: UtilityTariffByIdCacheEntry): void {
  entry.subscribers.forEach((subscriber) => subscriber(entry.state))
}

function shouldStartFetch(entry: UtilityTariffByIdCacheEntry): boolean {
  if (entry.fetchPromise) return false
  return !entry.hasResolved || entry.state.error
}

function maybeEvictEntry(cacheKey: string, entry: UtilityTariffByIdCacheEntry): void {
  if (entry.subscribers.size === 0 && !entry.fetchPromise) {
    // Keep successfully resolved entries so modal tab switches do not refetch.
    if (entry.hasResolved && !entry.state.error) {
      return
    }
    utilityTariffByIdCache.delete(cacheKey)
  }
}

async function runFetch(
  cacheKey: string,
  entry: UtilityTariffByIdCacheEntry,
  orgId: number,
  utilityTariffId: number,
  restClientInstance: UtilityTariffFetchClient
): Promise<void> {
  entry.state = { tariff: null, isLoading: true, error: false }
  notifySubscribers(entry)

  try {
    const response = await restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/utility_tariffs/${utilityTariffId}/`,
    })
    entry.state = {
      tariff: response?.data ?? null,
      isLoading: false,
      error: false,
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'utility_tariff_by_id_cache' },
      extra: { orgId, utilityTariffId },
    })
    entry.state = { tariff: null, isLoading: false, error: true }
  } finally {
    entry.hasResolved = true
    entry.fetchPromise = null
    notifySubscribers(entry)
    maybeEvictEntry(cacheKey, entry)
  }
}

function startFetch(
  cacheKey: string,
  entry: UtilityTariffByIdCacheEntry,
  orgId: number,
  utilityTariffId: number,
  restClientInstance: UtilityTariffFetchClient
): void {
  if (entry.fetchPromise) return

  entry.fetchPromise = runFetch(cacheKey, entry, orgId, utilityTariffId, restClientInstance)
}

export function subscribeToUtilityTariffById(
  orgId: number,
  utilityTariffId: number,
  restClientInstance: UtilityTariffFetchClient,
  subscriber: Subscriber
): () => void {
  const cacheKey = utilityTariffByIdCacheKey(orgId, utilityTariffId)
  let entry = utilityTariffByIdCache.get(cacheKey)

  if (!entry) {
    entry = {
      state: { ...EMPTY_UTILITY_TARIFF_BY_ID_STATE },
      subscribers: new Set(),
      fetchPromise: null,
      hasResolved: false,
    }
    utilityTariffByIdCache.set(cacheKey, entry)
  }

  entry.subscribers.add(subscriber)
  subscriber(entry.state)

  if (shouldStartFetch(entry)) {
    startFetch(cacheKey, entry, orgId, utilityTariffId, restClientInstance)
  }

  return () => {
    entry?.subscribers.delete(subscriber)
    if (entry) {
      maybeEvictEntry(cacheKey, entry)
    }
  }
}
